import React, { useState, useRef, useMemo } from 'react';
import classnames from 'classnames';
import { Table } from 'tea-component';
import { useForm } from 'react-final-form-hooks';
import GeneralModal from '@gp/components/GeneralModal';
import { useMemoizedFn, useDeepCompareEffect } from 'ahooks';
import { GAME_PILLAR_IMG_LIST } from '@gp/pages/Pipeline/ProjectDetails/GameDetail/constants';
import GamePillarImageUpload from '@gp/pages/Pipeline/ProjectDetails/GameDetail/components/GamePillar/components/GamePillarImageUpload';
import type { IBaseProps } from '@home/types/common';
import type { IMaterialVO } from '@gp/types/global';
import type { IMilestoneFinancialVO, IMilestoneFinancialForecastVO } from '@gp/types/milestones';
import type { IMilestone, IGRAction } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import './index.scss';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useAutoSaveParams } from '@gp/hooks/useAutoSaveParams';
import * as rbd from 'react-beautiful-dnd';

const { selectable, draggable, removeable, scrollable, autotip } = Table.addons;

export interface IUpdateGRFormModel {
  gate_review: string;
  gate_review_decision: string;
  dev_cost_actual: string;
  budget_next_milestone: string;
  approved_budget_to_next: string;
  dev_cost: string;
  key_milestones_financial: IMilestoneFinancialVO[];
  key_milestones_financial_forecast: IMilestoneFinancialForecastVO[];
  key_feedback: string;
  action_items: IGRAction[];
  next_milestone_scope: string;
  material: IMaterialVO[];
}

interface IUpdateGRModalProps extends IBaseProps {
  activeMilestone?: string;
  currentMilestoneDate?: string;
  // Milestones集合
  milestones?: IMilestone[];
  separatedMilestones?: IMilestone[];
  // 弹窗是否可见
  visible?: boolean;
  // 弹窗是否可见的setState
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  // confirm的回调
  onConfirm?: (payload: IUpdateGRFormModel) => void;
  // cancel的回调
  onCancel?: (payload: IUpdateGRFormModel) => void;
}

export interface IUpdateGRModalInstance {
  init: (payload: Partial<IUpdateGRFormModel>, currentMilestoneDate?: string) => void;
}

const DEFAULT_FORM_MODEL: IUpdateGRFormModel = {
  gate_review: '',
  gate_review_decision: '',
  dev_cost_actual: '',
  budget_next_milestone: '',
  approved_budget_to_next: '',
  dev_cost: '',
  key_milestones_financial: [],
  key_milestones_financial_forecast: [],
  key_feedback: '',
  action_items: [],
  next_milestone_scope: '',
  material: [],
};

const handleValidate = (values?: IUpdateGRFormModel): Record<string, string> => {
  // if (!values) return { milestone: FIELD_COMMON_ERR_MSG, date: FIELD_COMMON_ERR_MSG };
  // const errors: Record<string, string> = {};
  // const actionItemsValue = values?.action_items;
  // const keyMilestonesFinancialsValue = values?.key_milestones_financial;
  // const isKeyMilestonesFinancialsValid =
  //   !Array.isArray(keyMilestonesFinancialsValue) ||
  //   !keyMilestonesFinancialsValue.length ||
  //   keyMilestonesFinancialsValue?.every((keyMilestoneFinancial) => !isEmptyValue(keyMilestoneFinancial?.milestone));
  // const isActionItemsValid =
  //   !Array.isArray(actionItemsValue) ||
  //   !actionItemsValue.length ||
  //   actionItemsValue?.every((actionItem) => !isEmptyValue(actionItem?.due_date));
  // if (!isKeyMilestonesFinancialsValid) {
  //   errors.key_milestones_financial = FIELD_COMMON_ERR_MSG;
  // }
  // if (!isActionItemsValid) {
  //   errors.action_items = FIELD_COMMON_ERR_MSG;
  // }
  // return errors;
};

const GamePillarModal = (props: any, ref: any) => {
  const {
    className,
    visible = false,
    setVisible,
    onConfirm,
    onCancel,
    gamePillarDatas,
    pageKeyCur,
    imgList,
    setImgList,
    initialParams,
  } = props;

  // ImagesUploader组件实例
  const uploaderInstance = useRef(null);

  const [submitted, setSubmitted] = useState(false);
  const [isJustConfirm, setIsJustConfirm] = useState(false);
  const [isOptions, setIsOptions] = useState(false);

  const onDragEnd = useMemoizedFn((rec) => {
    const newSortList = rec?.map((item: any, index: number) => {
      return {
        ...item,
        sort: (index + 1)?.toString(),
      };
    });
    setImgList(newSortList);
    setIsOptions(true);
  });

  const handleConfirm = useMemoizedFn(async (values: IUpdateGRFormModel) => {
    await onConfirm?.(values);
    setSubmitted(false);
    form?.restart();
  });

  const handleCancel = useMemoizedFn(() => {
    setVisible?.(false);
    setSubmitted(false);
    form?.restart();
    onCancel?.(values);
  });

  const { form, values, submitting, handleSubmit } = useForm({
    initialValues: DEFAULT_FORM_MODEL,
    onSubmit: handleConfirm,
    validate: handleValidate,
  });

  const statusAllCur: any = useMemo(() => {
    const okList = imgList?.filter((item: any) => item.status === true && item.title !== '') || [];
    if (isJustConfirm && okList?.length < 3) {
      return false;
    }
    return true;
  }, [imgList, isJustConfirm]);

  const handleSubmitInPage = useMemoizedFn(() => {
    setSubmitted(true);
    const okList = imgList?.filter((item: any) => item.status === true && item.title !== '') || [];
    const emptyList =
      imgList?.filter((item: any) => item.status === true && item.title === '') || [];
    const errList = imgList?.filter((item: any) => item.status === false) || [];
    if ((okList?.length > 2 || emptyList?.length === 5) && errList?.length === 0) {
      onConfirm?.(imgList);
      setVisible?.(false);
    } else {
      setIsJustConfirm(true);
    }
  });

  const updateImgInfo = (payload: any) => {
    const valueTemp = {
      ...payload,
      // cover: '',
      operation_user: '',
    };

    const newList = imgList?.map((item: any) => (item.sort === payload.sort ? valueTemp : item));

    setIsJustConfirm(false);
    setImgList(newList);
  };

  // 表格配置
  const COLUMNS: any = [
    {
      key: 'img_id',
      render: (cvm: any, index: any) => {
        return (
          <div className="dbg-game-pillar-modal-table-item" key={index}>
            <GamePillarImageUpload
              ref={uploaderInstance}
              onChange={(payload: any) => {
                updateImgInfo(payload);
              }}
              imageData={cvm}
              statusAllCur={statusAllCur}
              isJustConfirm={isJustConfirm}
              isOptions={isOptions}
              setIsOptions={setIsOptions}
            />
          </div>
        );
      },
    },
  ];

  // 每隔5秒自动保存
  useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams,
    saveParamsCur: imgList,
    isBegin: visible,
    isChangeValue: true,
  });

  return (
    <GeneralModal
      className={classnames('dbg-game-pillar-modal', className)}
      // className={classnames('dbg-game-pillar-modal', className, { 'is-loading': isLoading })}
      title={'Game Pillar'}
      modalSize={'auto'}
      isFont14={true}
      // disabledConfirm={disabledConfirmCur}
      loadingConfirm={submitting}
      visible={visible}
      onConfirm={handleSubmitInPage}
      onClose={handleCancel}
      content={
        <div className="dbg-milestone-gr-edit font14">
          <div className="dbg-game-pillar-modal-des">{'Recommended ratio: 16:9 or 4:3.'}</div>
          <div className="dbg-game-pillar-modal-table">
            <Table
              records={imgList}
              recordKey="sort"
              rowDisabled={(record) => record?.loading === true}
              className={'dbg-game-pillar-modal-table-content'}
              columns={COLUMNS}
              hideHeader
              // addons={addons}
              addons={[
                autotip({
                  className: 'dbg-add-entities-table-autotip',
                  emptyText: 'Please select any Game first',
                }),
                draggable({
                  module: rbd,
                  onDragEnd,
                }),
              ]}
            />
          </div>
        </div>
      }
    />
  );
};

export default GamePillarModal;
